<template>
  <div class="row no-gutters align-items-center">
    <div class="col pr-2">
      <FormSelect
        :value="value.id"
        :disabled="!editable"
        v-bind="getNameSelectProps"
        dataTest="title"
        @input="updateFilter('id', $event)"
      />
    </div>
    <div class="col pr-2">
      <FormSelect
        :value="value.type"
        :disabled="!editable"
        v-bind="getTypeSelectProps"
        dataTest="filter"
        @input="updateFilter('type', $event)"
      />
    </div>
    <div class="col" data-test="value">
      <component
        :is="getValueComponent"
        v-if="isFilterWithValue(value.type)"
        :value="value.value"
        :disabled="!editable"
        :view="view"
        :product="view"
        v-bind="getValueProps"
        placeholder="Enter value..."
        @input="updateFilter('value', $event)"
      />
    </div>
  </div>
</template>

<script>
import _omit from 'lodash/omit'
import { INPUT_TYPES_TO_FILTER_TYPES_MAP } from '@/constants/'
import FormSelect from '@/components/Forms/FormSelect'
import FormText from '@/components/Forms/FormText'
import { toRefs } from '@vue/composition-api'
import useDynamicFilters from '@/components/composables/useDynamicFilters'

// TODO: This should be moved to separatedFile in form directory
// We are going to need BE Type to Component map in many places
// TODO: matchInputTypes can potentially be reused here
const INPUT_TYPES_MAP = {
  text: () => import('@/components/Forms/FormText'),
  date: () => import('@/components/Forms/FormDate'),
  time: () => import('@/components/Forms/FormTime'),
  number: () => import('@/components/Forms/FormNumber'),
  select: () => import('@/components/Forms/FormSelect'),
  boolean: () => import('@/components/Forms/FormYesNo'),
  // useSectionFilters specific types
  attributeSelect: () => import('@/components/Forms/FormAttributeSelect'),
  campusSelect: () => import('@/components/Forms/FormCampusSelect'),
  locationSelect: () => import('@/components/Forms/FormLocationSelect'),
  gradeModeSelect: () => import('@/components/Forms/FormGradeModeSelect'),
  gradeModesMultiSelect: () => import('@/components/Forms/FormGradeModesMultiSelect'),
  days: () => import('@/components/DaysSelect'),
  room: () => import('@/components/Forms/FormRoomSelect'),
  careerSelect: () => import('@/components/Forms/FormCareerSelect'),
  cipCodeSelect: () => import('@/components/Forms/FormCipCodeSelect'),
  hegisCodeSelect: () => import('@/components/Forms/FormHegisCodeSelect'),
  milestoneSelect: () => import('@/components/Forms/FormMilestoneSelect'),
  requirementDesignationSelect: () => import('@/components/Forms/FormRequirementDesignationSelect'),
  subjectCodeSelect: () => import('@/components/Forms/Variations/subjectCodeSelect'),
  fieldOfStudySelect: () => import('@/components/Forms/FormFieldOfStudySelect'),
  effectiveStartDate: () => import('@/components/Forms/FormEffectiveStartDate'),
  effectiveEndDate: () => import('@/components/Forms/FormEffectiveEndDate'),
  programLevelSelect: () => import('@/components/Forms/FormProgramLevelSelect'),
  degreeSelect: () => import('@/components/Forms/FormDegreeSelect'),
  collegeSelect: () => import('@/components/Forms/FormCollegeSelect'),
  courseSelect: () => import('@/components/Forms/FormCourseSelect'),
  sectionSelect: () => import('@/components/Forms/FormSectionSelect'),
  // eslint-disable-next-line no-restricted-imports
  programSelect: () => import('@cm/client/components/Forms/FormProgramSelect'),
  nscClassificationSelect: () => import('@/components/Forms/FormNscClassificationSelect'),
  sulaSpecialProgramSelect: () => import('@/components/Forms/FormSulaSpecialProgramSelect'),
  componentSelect: () => import('@/components/Forms/FormComponentSelect'),
  tuitionGroupSelect: () => import('@/components/Forms/FormTuitionGroupSelect'),
  termSelect: () => import('@/components/Forms/FormTermSelect'),
  requirementGroupSelect: () => import('@/components/Forms/FormRequirementGroupSelect'),
}
const NAMES_MAP = {
  partOfTerm: () => import('@/components/Forms/specific/PartsOfTermSelect'),
  preferredBuilding: () => import('@/components/Forms/specific/BuildingsSelect'),
  preferredBuildings: () => import('@/components/Forms/specific/BuildingsSelect'),
  preferredRoomFeatures: () => import('@/components/Forms/specific/PreferredRoomFeaturesSelect'),
  preferredRoomType: () => import('@/components/Forms/specific/PreferredRoomTypeSelect'),
  preferredRooms: () => import('@/components/Forms/specific/PreferredRoomsSelect'),
}
const VALUELESS_FILTER_TYPES = [ 'isEmpty', 'isNotEmpty' ]
const ALLOW_TEXT_FILTER_TYPES = [ 'contains', 'doesNotContain' ]

export default {
  components: { FormSelect, FormText },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    view: {
      type: String,
      default: 'em',
    },
    fields: {
      type: Array,
      default: () => [],
    },
    types: {
      type: Object,
      default: () => INPUT_TYPES_TO_FILTER_TYPES_MAP,
    },
    editable: {
      type: Boolean,
      default: true,
    },
  },
  setup (props) {
    const { fields, types } = toRefs(props)
    const { getFilterById, fieldsFormatted } = useDynamicFilters(fields, types)

    return {
      getFilterById,
      fieldsFormatted,
    }
  },
  computed: {
    // TODO: Computed prop names starting with get* should be renamed so it does not look like methods
    getNameSelectProps () {
      return {
        trackBy: 'id',
        config: {
          options: this.fieldsFormatted,
          trackBy: 'id',
          label: 'label',
        },
      }
    },
    getTypeSelectProps () {
      const possibleInputType = this.value.inputType || this.value.questionType
      const types = this.types[possibleInputType] || this.types.text
      return {
        trackBy: 'key',
        config: {
          options: types,
          trackBy: 'key',
          label: 'label',
        },
      }
    },
    getValueProps () {
      return this.fieldsFormatted.find(f => f.id === this.value.id)?.props || {}
    },
    getValueComponent () {
      const filterField = this.fieldsFormatted.find(f => f.id === this.value.id) || {}

      // For Single Select Inputs, certain filter types require a text input to be provided so users can filter on generic text values
      // instead of being required to select from list of options
      if (ALLOW_TEXT_FILTER_TYPES.includes(this.value.type) && filterField.showTextForContains) return INPUT_TYPES_MAP.text

      // For some cases it's just easier to use `name` instead of `inputType`,
      // to not clutter BE with some additional logic.
      // EXAMPLE: `partOfTerm` has inputType=PartsOfTermSelect,
      // which is basically just inputType=select, but with some additional options.
      // For BE it should still be just select, it's only FE specific logic.
      return (
        NAMES_MAP[filterField.name] ||
        INPUT_TYPES_MAP[filterField.inputType] ||
        INPUT_TYPES_MAP[filterField.questionType] ||
        INPUT_TYPES_MAP.text
      )
    },
  },
  methods: {
    updateFilter (key, value) {
      const updatedFilter = {
        ...(this.value || {}),
        ...(key === 'id' ? this.getFilterById(value) : {}),
        [key]: value,
      }

      const isFilterWithValue = this.isFilterWithValue(updatedFilter.type)
      this.$emit('input', isFilterWithValue ? updatedFilter : _omit(updatedFilter, [ 'value' ]))
    },
    isFilterWithValue (filterType) {
      return !VALUELESS_FILTER_TYPES.includes(filterType)
    },
  },
}
</script>
